import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { BUBBLES } from "./const/bubbles.const";
import { CONTACT_CENTER_BEHIND_MODALS_Z_INDEX, isEventInsideAppModal, useAppModalStack } from "./useAppModalStack";

const Z_INDEX = {
  base: 999900,
  inactive: 999910,
  active: 999999,
  callNotification: 1000100,
};

const FloatingBubblesContext = createContext(null);

export const FloatingBubblesProvider = ({ children }) => {
  /**
   * ESTADO BASE
   * Cada key debe existir en BUBBLES
   */
  const [openBubbles, setOpenBubbles] = useState({
    [BUBBLES.MESSAGES]: false,
    [BUBBLES.CALLS]: false,
  });
  const [contactCenterRaised, setContactCenterRaised] = useState(false);
  const modalStack = useAppModalStack();
  const previousModalCountRef = useRef(0);

  /**
   * ─────────────────────────────────────────────
   * ACCIONES GENERICAS (ESCALABLES)
   * ─────────────────────────────────────────────
   */

  const openBubble = useCallback((bubble) => {
    setOpenBubbles((prev) => ({
      ...prev,
      [bubble]: true,
    }));
  }, []);

  const closeBubble = useCallback((bubble) => {
    setOpenBubbles((prev) => ({
      ...prev,
      [bubble]: false,
    }));
  }, []);

  const toggleBubble = useCallback((bubble) => {
    setOpenBubbles((prev) => ({
      ...prev,
      [bubble]: !prev[bubble],
    }));
  }, []);

  /**
   * ─────────────────────────────────────────────
   * ATAJOS SEMANTICOS (DX)
   * Estos NO son obligatorios, pero mejoran DX
   * ─────────────────────────────────────────────
   */

  const openMessages = () => openBubble(BUBBLES.MESSAGES);
  const closeMessages = () => closeBubble(BUBBLES.MESSAGES);
  const toggleMessages = () => toggleBubble(BUBBLES.MESSAGES);

  const openCalls = () => openBubble(BUBBLES.CALLS);
  const closeCalls = () => closeBubble(BUBBLES.CALLS);
  const toggleCalls = () => toggleBubble(BUBBLES.CALLS);

  /**
   * ─────────────────────────────────────────────
   * ESTADO DERIVADO (REGLAS DE UI)
   * ─────────────────────────────────────────────
   */
  const derived = useMemo(() => {
    const activeBubbles = Object.entries(openBubbles)
      .filter(([, open]) => open)
      .map(([key]) => key);

    const anyBubbleOpen = activeBubbles.length > 0;

    /**
     * PRIORIDAD VISUAL
     * Define aquí qué burbuja va arriba
     */
    const activeBubble =
      openBubbles[BUBBLES.CALLS]
        ? BUBBLES.CALLS
        : openBubbles[BUBBLES.MESSAGES]
          ? BUBBLES.MESSAGES
          : null;

    return {
      activeBubbles,
      activeBubble,
      anyBubbleOpen,
    };
  }, [openBubbles]);

  useEffect(() => {
    const previousCount = previousModalCountRef.current;
    if (modalStack.count > previousCount) {
      setContactCenterRaised(false);
    }
    if (modalStack.count === 0) {
      setContactCenterRaised(false);
    }
    previousModalCountRef.current = modalStack.count;
  }, [modalStack.count]);

  const raiseContactCenter = useCallback(() => {
    setContactCenterRaised(true);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (isEventInsideAppModal(event.target)) {
        setContactCenterRaised(false);
      }
    };
    const handleModalActivate = () => {
      setContactCenterRaised(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("app-modal-activate", handleModalActivate);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("app-modal-activate", handleModalActivate);
    };
  }, []);

  const contactCenterZIndex = useMemo(() => {
    const normal = derived.activeBubble === BUBBLES.CALLS ? Z_INDEX.active : Z_INDEX.inactive;
    if (modalStack.count === 0) return normal;
    if (contactCenterRaised) return normal;
    return CONTACT_CENTER_BEHIND_MODALS_Z_INDEX;
  }, [contactCenterRaised, derived.activeBubble, modalStack.count]);

  const chatDockZIndex = useMemo(() => {
    const normal = Z_INDEX.inactive - 1;
    if (modalStack.count === 0) return normal;
    if (contactCenterRaised) return normal;
    return CONTACT_CENTER_BEHIND_MODALS_Z_INDEX - 1;
  }, [contactCenterRaised, modalStack.count]);

  return (
    <FloatingBubblesContext.Provider
      value={{
        // estado base
        openBubbles,

        // flags directos (compatibilidad)
        messagesOpen: openBubbles[BUBBLES.MESSAGES],
        callsOpen: openBubbles[BUBBLES.CALLS],

        // derivados
        ...derived,

        // acciones genericas
        openBubble,
        closeBubble,
        toggleBubble,

        // acciones semanticas
        openMessages,
        closeMessages,
        toggleMessages,
        openCalls,
        closeCalls,
        toggleCalls,
        raiseContactCenter,

        // tokens UI
        zIndex: Z_INDEX,
        contactCenterZIndex,
        chatDockZIndex,
      }}
    >
      {children}
    </FloatingBubblesContext.Provider>
  );
};

export const useFloatingBubbles = () => {
  const ctx = useContext(FloatingBubblesContext);
  if (!ctx) {
    throw new Error(
      "useFloatingBubbles use inside of FloatingBubblesProvider"
    );
  }
  return ctx;
};

export const useOptionalFloatingBubbles = () => {
  return useContext(FloatingBubblesContext);
};

